/* ────────────────────────────────────────────────────────────────
   RS Swatch Country — conditional "Other" country field

   Shows a free-text country input when #SwatchForm-country reads
   "Other", and hides it again otherwise. Scoped entirely to the
   swatch modal; touches no other surface.

   ── Why `required` lives in the MARKUP and is REMOVED here ───────
   assets/rs-form-validate.js (shared with rs-contact-hero — not
   editable from this arc) snapshots its field list ONCE, inside
   bindForm on DOMContentLoaded:

     form.querySelectorAll('input[required], select[required], textarea[required]')

   That array is never recomputed, and bindForm is closed over inside
   its IIFE. So a field that is non-required at DOM-ready is invisible
   to the validator FOREVER — setting `required` on it later does not
   enrol it, and it would submit empty with no error.

   The inversion that fixes it: ship `required` in the markup so the
   field IS in the snapshot, then strip the attribute here whenever the
   select is not "Other". The validator holds a live element reference
   and re-reads input.validity at submit time, so a stripped `required`
   yields valueMissing === false and the field passes silently while
   hidden. Restoring it re-arms the same validated field. Both
   directions work without the validator knowing anything changed.

   THEREFORE: this script must run AFTER rs-form-validate.js. Both are
   deferred, so document order decides, and the snippet emits them in
   that order. If the snapshot were taken after the strip, the field
   would drop out of validation entirely.

   ── Why a hidden `required` is not a no-JS trap here ─────────────
   Normally a hidden required input blocks submit and focuses an
   invisible element. Not on this form, for two independent reasons:
     1. The form carries novalidate, so native constraint validation
        never runs on submit.
     2. The modal is a <dialog> with no `open` attribute, opened only
        by rs-modal.js — with JS off it never renders, so the form is
        unreachable rather than broken.
   If novalidate is ever removed from the form tag, re-verify reason 1;
   reason 2 stands on its own. Paired comment in the snippet markup.
   ──────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.rsSwatchCountryLoaded) return;
  window.rsSwatchCountryLoaded = true;

  var SELECT_ID = 'SwatchForm-country';
  var INPUT_ID = 'SwatchForm-country-other';
  var FIELD_SELECTOR = '[data-rs-country-other-field]';
  var HIDDEN_CLASS = 'rs-swatch-req__field--hidden';
  var OTHER_VALUE = 'Other';
  /* Expando on the <select>, mirroring rs-modal.js's BOUND_FLAG pattern —
     the element, not a module-level boolean, so a re-init against a
     replaced node still binds while the surviving node does not double-bind. */
  var BOUND_FLAG = '__rsSwatchCountryBound';

  /* Clears every piece of state rs-form-validate.js's validateField()
     writes on failure, so a hidden field can never keep a stale error:
       • the sink's `hidden` attribute        (sink.removeAttribute('hidden'))
       • the sink's [data-rs-error-text] copy (textEl.textContent = message)
       • .is-invalid on the [data-rs-field]   (wrapper.classList.add)
       • aria-invalid on the input
       • aria-describedby on the input
     Kept in sync with that function by hand — it is not exported. */
  function clearErrorState(input, field) {
    var sink = document.getElementById(input.id + '-error');
    if (sink) {
      var textEl = sink.querySelector('[data-rs-error-text]');
      if (textEl) textEl.textContent = '';
      sink.setAttribute('hidden', '');
    }
    if (field) field.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
    input.removeAttribute('aria-describedby');
  }

  function apply(select, input, field) {
    if (select.value === OTHER_VALUE) {
      field.classList.remove(HIDDEN_CLASS);
      input.setAttribute('required', '');
      input.setAttribute('aria-required', 'true');
      return;
    }
    field.classList.add(HIDDEN_CLASS);
    input.removeAttribute('required');
    input.removeAttribute('aria-required');
    input.value = '';
    clearErrorState(input, field);
  }

  function bind() {
    var select = document.getElementById(SELECT_ID);
    var input = document.getElementById(INPUT_ID);
    if (!select || !input) return;

    var field = input.closest(FIELD_SELECTOR);
    if (!field) return;

    /* Set the initial state even when already bound: a re-init (e.g.
       shopify:section:load) should re-sync paint without a second listener. */
    apply(select, input, field);

    if (select[BOUND_FLAG]) return;
    select[BOUND_FLAG] = true;

    select.addEventListener('change', function () {
      apply(select, input, field);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  document.addEventListener('shopify:section:load', bind);
})();
